import { Box, Button, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type JSX } from "react";

import type {
  SalesStrategyFold,
  SalesSteeringActivePayload,
  SalesSteeringDirective,
} from "@xop/dashboard-shared";
import {
  foldChipLabel,
  foldFailureReason,
  isFoldInFlight,
  STEER_TARGET_LABELS,
  steerTarget,
} from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";
import { describeAgentError } from "../describeAgentError.js";

/**
 * "Active directives" — the standing steering orders the agent is
 * currently honouring. v2 steering: a note the operator drops is a
 * STANDING directive (it shapes every mission-refill, strategy regen,
 * and the master loop until archived), not a one-off hint. This panel
 * makes that visible + gives the operator the only retire control:
 *
 *   - lists each active directive, newest first
 *   - shows whether the agent has acknowledged it yet
 *   - an Archive button retires it (reversible on the agent side)
 *
 * Reads `/api/sales/v2/steering/active`. Hidden entirely when there are
 * no active directives so the Today tab stays calm on a fresh agent.
 */
/** 5 s while any directive has a fold in flight, 30 s otherwise — fast
 *  enough that "folding…" turns into "folded" while the operator is still
 *  looking, without hammering the poll endpoint on an otherwise-idle page.
 *  Pulled out of the `useQuery` options object because it is a pure function
 *  of the query data: exercising the switch through React Query's own timers
 *  would mean driving fake timers through a real poll cycle for no benefit
 *  over calling this directly. */
export function activeDirectivesRefetchInterval(
  data: SalesSteeringActivePayload | undefined,
): number {
  return (data?.active ?? []).some((d) => isFoldInFlight(d.fold)) ? 5_000 : 30_000;
}

export function ActiveDirectives(): JSX.Element | null {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();

  const active = useQuery({
    queryKey: ["sales", "steering", "active"],
    queryFn: salesApi.listActiveSteering,
    // A fold takes tens of seconds, and the operator is watching a chip that
    // says so. Poll fast enough that "folding…" turns into "folded" while they
    // are still looking, and drop back to the calm cadence when nothing is in
    // flight — this panel is otherwise idle on most page loads.
    refetchInterval: (query) => activeDirectivesRefetchInterval(query.state.data),
    refetchIntervalInBackground: false,
  });

  const archive = useMutation({
    mutationFn: (id: string) => salesApi.archiveSteering(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales", "steering", "active"] });
      qc.invalidateQueries({ queryKey: ["sales", "today"] });
    },
  });

  // Retry errors are keyed by directive id, not just held on the mutation:
  // `retryFold` is ONE shared mutation across every directive in the list, so
  // `retryFold.isPending` / a bare `retryFold.error` would disable and flag
  // every OTHER directive's "Try again" while one retry is in flight — the
  // exact defect this panel exists to avoid on the fold itself. Scoping by id
  // keeps each directive's retry state its own.
  const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});

  const retryFold = useMutation({
    mutationFn: (id: string) => salesApi.refoldSteering(id),
    onMutate: (id: string) => {
      setRetryErrors((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    onSuccess: (_data, id: string) => {
      setRetryErrors((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      qc.invalidateQueries({ queryKey: ["sales", "steering", "active"] });
      qc.invalidateQueries({ queryKey: ["sales", "strategy-regen"] });
    },
    onError: (err, id: string) => {
      setRetryErrors((prev) => ({ ...prev, [id]: describeAgentError(err, "something went wrong") }));
    },
  });

  const directives = active.data?.active ?? [];

  // A directive that leaves the list (archived, or its steer got consumed)
  // must not leave a stale entry behind in `retryErrors` — otherwise a retry
  // failure recorded against an id that no longer renders just sits in state
  // for the rest of the session. Bounded by session length either way, but
  // pruning on every list change keeps the map honest.
  useEffect(() => {
    if (!active.data) return;
    const liveIds = new Set(active.data.active.map((d) => d.id));
    setRetryErrors((prev) => {
      const stale = Object.keys(prev).filter((id) => !liveIds.has(id));
      if (stale.length === 0) return prev;
      const next = { ...prev };
      for (const id of stale) delete next[id];
      return next;
    });
  }, [active.data]);

  if (directives.length === 0) return null;

  return (
    <Box
      component="section"
      aria-label="Active directives"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 2,
      }}
    >
      <Typography component="h2" sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        Active directives — the agent is steering toward these
      </Typography>
      <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
        {directives.map((d) => (
          <Box
            component="li"
            key={d.id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.default",
              px: 1.5,
              py: 1,
            }}
          >
            <Box sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: "text.primary" }}>{d.body}</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                <TargetPill target={d.target} />
                <StatusPill status={d.status} />
                <FoldPill fold={d.fold} />
                <Box component="span" sx={{ fontSize: 11, color: "text.disabled" }}>
                  {missionLine(d)}
                </Box>
              </Box>
              {d.verdict ? (
                <VerdictBlock verdict={d.verdict} action={d.verdict_action ?? null} at={d.verdict_at ?? null} />
              ) : d.status === "exhausted" ? (
                <Box component="span" sx={{ fontSize: 11, lineHeight: 1.45, color: "warning.main" }}>
                  Tried everything in its mission budget — the agent is writing its
                  verdict on what it found and what to do next; it&apos;ll appear here
                  shortly.
                </Box>
              ) : null}
              {d.fold?.state === "failed" ? (
                <FoldFailure
                  reason={d.fold.error}
                  onRetry={canEdit ? () => retryFold.mutate(d.id) : null}
                  retrying={retryFold.isPending && retryFold.variables === d.id}
                  retryError={retryErrors[d.id] ?? null}
                  body={d.body}
                />
              ) : null}
              <Box component="span" sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
                {new Date(d.created_at).toLocaleString()}
              </Box>
            </Box>
            {canEdit && (
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => archive.mutate(d.id)}
                disabled={archive.isPending}
                aria-label={`Archive directive: ${d.body}`}
                sx={{ flexShrink: 0, fontSize: 12, fontWeight: 500, color: "text.secondary", px: 1, py: 0.5 }}
              >
                Archive
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ── Per-steer status pill ─────────────────────────────────────── */

type Tone = "good" | "warn" | "neutral";
type SteerStatus = NonNullable<SalesSteeringDirective["status"]>;

const STATUS_META: Record<SteerStatus, { label: string; tone: Tone }> = {
  queued: { label: "Queued", tone: "neutral" },
  pursuing: { label: "Pursuing", tone: "good" },
  acting: { label: "Acting", tone: "good" },
  exhausted: { label: "Budget spent", tone: "warn" },
};

function toneColor(t: Theme, tone: Tone): string {
  return tone === "good"
    ? t.palette.success.main
    : tone === "warn"
      ? t.palette.warning.main
      : t.palette.text.disabled;
}

function Pill({ label, tone }: { label: string; tone: Tone }): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        border: 1,
        px: 0.875,
        py: 0.125,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: (t: Theme) => toneColor(t, tone),
        bgcolor: (t: Theme) => alpha(toneColor(t, tone), 0.12),
        borderColor: (t: Theme) => alpha(toneColor(t, tone), 0.4),
      }}
    >
      {label}
    </Box>
  );
}

function StatusPill({ status }: { status?: SalesSteeringDirective["status"] }): JSX.Element {
  const meta = STATUS_META[status ?? "queued"];
  return <Pill label={meta.label} tone={meta.tone} />;
}

/** Which agent the directive aims at — Lead Generator / Harvester / Both. Always
 * shown so an operator can tell a harvester steer ("harvest just the US") from a
 * lead steer at a glance. Coerced via `steerTarget` so a legacy/absent value
 * reads as a Lead-Generator steer. */
function TargetPill({ target }: { target?: SalesSteeringDirective["target"] }): JSX.Element {
  return <Pill label={STEER_TARGET_LABELS[steerTarget(target)]} tone="neutral" />;
}

/** The fold's state as a pill — or nothing.
 *
 * A failure deliberately renders NO pill: it gets a full sentence below,
 * because "failed" alone is what makes an operator re-send a note that was
 * saved all along. */
function FoldPill({ fold }: { fold?: SalesStrategyFold | null | undefined }): JSX.Element | null {
  const label = foldChipLabel(fold);
  if (!label) return null;
  return <Pill label={label} tone={fold?.state === "done" ? "good" : "neutral"} />;
}

/** The note IS saved and IS still shaping the agent's work — only the memo
 *  rewrite failed. Saying so is the whole point: a bare "failed" over committed
 *  work is what invites a duplicate re-send. */
function FoldFailure({
  reason,
  onRetry,
  retrying,
  retryError,
  body,
}: {
  reason: string;
  onRetry: (() => void) | null;
  retrying: boolean;
  retryError: string | null;
  body: string;
}): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 0.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Typography role="alert" sx={{ fontSize: 11, lineHeight: 1.45, color: "warning.main" }}>
          Couldn&apos;t fold this into the strategy — {foldFailureReason(reason)}. The
          directive is saved and still active; the agent will honour it on its
          next cycle.
        </Typography>
        {onRetry && (
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={onRetry}
            disabled={retrying}
            aria-label={`Try again: fold "${body}" into the strategy`}
            sx={{ flexShrink: 0, fontSize: 11, fontWeight: 500, color: "text.secondary", px: 1, py: 0.25 }}
          >
            Try again
          </Button>
        )}
      </Box>
      {/* A retry that itself fails gets its own line rather than repeating
          "saved and still active" (already said above, two pixels up) — the
          new information here is only that the retry attempt didn't land. */}
      {retryError ? (
        <Typography role="alert" sx={{ fontSize: 11, lineHeight: 1.45, color: "warning.main" }}>
          Couldn&apos;t retry — {retryError}. Nothing changed — it&apos;s still
          saved and still active.
        </Typography>
      ) : null}
    </Box>
  );
}

/* ── Exhaustion verdict — the agent's "I tried everything" report ──── */

type VerdictAction = NonNullable<SalesSteeringDirective["verdict_action"]>;

const VERDICT_META: Record<VerdictAction, { label: string; tone: Tone; hint: string }> = {
  reframe: {
    label: "Reframe suggested",
    tone: "warn",
    hint: "The theme is sound but the literal target isn't groundable from public sources — re-issue with a more findable angle.",
  },
  archive: {
    label: "Safe to archive",
    tone: "neutral",
    hint: "Nothing left to pursue here — archive to free the queue.",
  },
  keep: {
    label: "Keep pursuing",
    tone: "good",
    hint: "Still promising — the agent recommends giving it more budget.",
  },
};

/** Renders the agent's first-person verdict once a steer is exhausted: what it
 * tried, why it could/couldn't ground it, and a recommendation (reframe /
 * archive / keep). This is the operator's window into WHY the agent is moving
 * on, so a taper never looks like a silent drift. */
function VerdictBlock({
  verdict,
  action,
  at,
}: {
  verdict: string;
  action: SalesSteeringDirective["verdict_action"];
  at?: string | null;
}): JSX.Element {
  const meta = action ? VERDICT_META[action] : null;
  return (
    <Box
      sx={{
        display: "grid",
        gap: 0.5,
        mt: 0.25,
        borderRadius: 1,
        borderLeft: 3,
        borderColor: (t: Theme) => (meta ? alpha(toneColor(t, meta.tone), 0.6) : "divider"),
        bgcolor: (t: Theme) => alpha(t.palette.text.primary, 0.03),
        pl: 1.25,
        pr: 1,
        py: 0.875,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
          Agent verdict · tried everything
        </Box>
        {meta ? <Pill label={meta.label} tone={meta.tone} /> : null}
      </Box>
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: "text.primary", whiteSpace: "pre-line" }}>
        {verdict}
      </Typography>
      {meta ? (
        <Box component="span" sx={{ fontSize: 11, lineHeight: 1.4, color: "text.disabled" }}>
          {meta.hint}
        </Box>
      ) : null}
      {at ? (
        <Box component="span" sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          Verdict {new Date(at).toLocaleString()}
        </Box>
      ) : null}
    </Box>
  );
}

function missionLine(d: SalesSteeringDirective): string {
  const total = d.missions_total ?? 0;
  if (total === 0) {
    return d.acknowledged ? "acknowledged · no missions yet" : "not yet picked up";
  }
  const active = d.missions_active ?? 0;
  const base = `drove ${total} mission${total === 1 ? "" : "s"}`;
  return active > 0 ? `${base} · ${active} running now` : base;
}
